import './styles.scss'

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { shallowEqual } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import Skeleton from 'react-loading-skeleton'

import { ChevronRightIcon, HeartIcon } from '@/components/Icons'
import NotFound from '@/components/NotFound'
import ProductComparisonModal from '@/components/ProductComparisonModal'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  selectCurrencyUnit,
  selectDeliveryDates,
  selectHasMinQuantity,
  selectIsLoadingPriceBox,
  selectIsProductDangerous,
  selectNetPrice,
} from '@/store/selectors/cartSelector'
import {
  getIsLoadingProductDetail,
  getProductDetailData,
  getProductFetchStatus,
} from '@/store/selectors/productSelector'
import { trackCategory } from '@/store/slices/adobeAnalyticSlice'
import { getPriceBoxData, resetPriceBoxData } from '@/store/slices/cartSlice'
import { productActions } from '@/store/slices/productSlice'
import { sendProductDetail, showAddProductToWishlistModal } from '@/store/slices/wishlistSlice'
import { useDeviceDetect } from '@/hooks'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useCalculateScrollbarWidth from '@/hooks/useCalculateScrollbarWidth'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import usePermission from '@/hooks/usePermission'
import PermissionWrapper from '@/HOCs/permissionWrapper'
import { ACTION_STATUS, SHOP_CATEGORY_TYPE } from '@/libs/constants'
import { PAGE_TYPES, TRACK_DATA } from '@/libs/constants/adobeAnalytics'
import BREAKPOINTS from '@/libs/constants/breakpoints'
import {
  MODULE_SHOP_NAVIGATION_RIGHTS,
  MODULE_WISHLIST_RIGHTS,
} from '@/libs/constants/modulerights'
import PRODUCT_TYPES from '@/libs/constants/productTypes'
import { getCategoriesFromProductDetail } from '@/libs/utils/adobeAnalysis'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator, removeLangCodesPrefix } from '@/libs/utils/language'
import { getMainProductImage } from '@/libs/utils/productionDetail'

import PriceBox from './PriceBox'
import ProductCarousel from './ProductCarousel'
import ProductDetail from './ProductDetails'

interface ProductDetailPageProps {
  title: string
  pageId: string
  breadcrumbs: {
    title: string
    path: string
    dynamicTitle?: boolean
    dynamicPath?: boolean
  }[]
}

function ProductDetailPage(props: ProductDetailPageProps) {
  const { title, breadcrumbs, pageId } = props
  const { setTitle, setIsTitleTranslated } = useDocumentTitle(title)
  const [setBreadcrumbsData] = useBreadcrumbs(breadcrumbs)
  const { isIpadLandscape } = useDeviceDetect()
  const isLargerThan992 = useMediaQuery(`(min-width:${BREAKPOINTS.lg}px)`)

  const [showProductComparisonModal, setShowProductComparisonModal] = useState(false)

  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { materialNumber } = useParams()
  useCalculateScrollbarWidth()

  const { setPageInfoData } = useAdobeAnalysis(TRACK_DATA.CATEGORIES)

  const {
    materialName,
    categoryName,
    categoryPath,
    type: productType,
    images: productImages,
    isAvailable,
    requiresInstruction,
    isBProgramProduct,
    listPrice,
    currencyUnit: productDetailCurrencyUnit,
    displayListPrice,
  } = useAppSelector(getProductDetailData, shallowEqual)

  const { hasPermission: hasViewListPricePermission } = usePermission(
    MODULE_SHOP_NAVIGATION_RIGHTS.VIEW_LIST_PRICE,
  )

  const currencyUnit = useAppSelector(selectCurrencyUnit) || productDetailCurrencyUnit
  const netPrice = useAppSelector(selectNetPrice)
  const deliveryDates = useAppSelector(selectDeliveryDates)
  const isDangerous = useAppSelector(selectIsProductDangerous)
  const hasMinQuantity = useAppSelector(selectHasMinQuantity)
  const isLoadingProductDetail = useAppSelector(getIsLoadingProductDetail)
  const isLoadingPriceBox = useAppSelector(selectIsLoadingPriceBox)
  const actionStatus = useAppSelector(getProductFetchStatus)

  const productTypeLinkPrefix = useMemo(() => {
    const result = /^\/accessories|\/detergents/.exec(location.pathname)
    return isValidArray(result) ? result[0] : ''
  }, [location.pathname])

  const handleAddToWishlist = () => {
    dispatch(
      sendProductDetail({
        materialName,
        materialNumber,
        img: getMainProductImage(productImages),
      }),
    )
    dispatch(showAddProductToWishlistModal())
  }

  const renderActionButtons = () => {
    if (!isLargerThan992) return null

    return (
      <>
        <PermissionWrapper permission={MODULE_SHOP_NAVIGATION_RIGHTS.PRODUCT_COMPARISION}>
          <button
            className="compare-product-btn"
            type="button"
            onClick={() => setShowProductComparisonModal(true)}
          >
            <ChevronRightIcon />
            {t('productComparison.button')}
          </button>
        </PermissionWrapper>
        <PermissionWrapper permission={MODULE_WISHLIST_RIGHTS.ADD_PRODUCT_CHANGE_WISHLIST}>
          <button className="button-as-link" type="button" onClick={handleAddToWishlist}>
            <HeartIcon className="add-to-wishlist__button" />
          </button>
        </PermissionWrapper>
      </>
    )
  }

  useEffect(() => {
    if (materialName) {
      setPageInfoData({
        pageId,
        pageType: PAGE_TYPES.PRODUCT_DETAILS,
        pageName: `product_detail:${materialName}`,
      })
    }
  }, [pageId, setPageInfoData, materialName])

  useEffect(() => {
    dispatch(productActions.getProductDetailsByLanguage(materialNumber))
    dispatch(
      getPriceBoxData({
        materialNumber,
      }),
    )

    return () => {
      dispatch(productActions.setMaterialNumberToEmpty())
      dispatch(resetPriceBoxData())
    }
  }, [dispatch, materialNumber])

  useEffect(() => {
    if (isValidArray(categoryPath)) {
      const categories = getCategoriesFromProductDetail(categoryPath)
      dispatch(trackCategory(categories))
    }
  }, [categoryPath, dispatch])

  useEffect(() => {
    if (materialName) {
      setIsTitleTranslated(true)
      setTitle(materialName)
    }

    const breadcrumbsData = []

    const updatePathBasedOnCategory = (type) => {
      const productTypePath = `/${type}`

      // Example: prefix already added '/detergents'
      // when changing to new product with category '/accessories'
      // The path will be '/accessories/detergents/product/...'
      if (productTypeLinkPrefix !== productTypePath) {
        // if current prefix is different with previous prefix, remove it.
        const regex = new RegExp(
          `/${SHOP_CATEGORY_TYPE.DETERGENTS}|/${SHOP_CATEGORY_TYPE.ACCESSORIES}`,
          'i',
        )
        const removedPrefixPath = location.pathname.replace(regex, '')
        // remove the language code then adding new category type of the prefix
        // or it will be    /de-at/detergent/de-at/product/1.234-567.8
        const link = `/${type}${removeLangCodesPrefix(removedPrefixPath)}`
        globalThis.history.replaceState(null, '', linkGenerator(link))
      } else {
        breadcrumbsData.push({
          titleData: [t(`breadcrumbTitle.${type}`)],
          pathData: [`${type}`],
        })
      }
    }

    switch (productType) {
      case PRODUCT_TYPES.ACCESSORY: {
        updatePathBasedOnCategory(SHOP_CATEGORY_TYPE.ACCESSORIES)
        break
      }
      case PRODUCT_TYPES.DETERGENT: {
        updatePathBasedOnCategory(SHOP_CATEGORY_TYPE.DETERGENTS)
        break
      }
      default: {
        break
      }
    }
    if (categoryPath) {
      switch (categoryPath.length) {
        case 1: {
          breadcrumbsData.push(
            {}, // Category Overview
            {
              // Product Overview
              titleData: [categoryPath[0].name],
              pathData: [categoryPath[0].id, categoryPath[0].id], // Same id
            },
            {}, // Product Overview with class selected
          )
          // Example result:
          // breadcrumbsData = [
          //   {},
          //   {
          //     titleData: ['Outdoor Power Equipment'],
          //     pathData: ['11023641', '11023641'],
          //   },
          //   {},
          // ];
          break
        }
        case 2: {
          breadcrumbsData.push(
            {
              // Category Overview
              titleData: [categoryPath[0].name],
              pathData: [categoryPath[0].id],
            },
            {
              // Product Overview
              titleData: [categoryPath[1].name],
              pathData: [categoryPath[0].id, categoryPath[1].id],
            },
            {}, // Product Overview with class selected
          )
          // Example result:
          // breadcrumbsData = [
          //   {
          //     titleData: ['Pressure Washer'],
          //     pathData: ['11051607'],
          //   },
          //   {
          //     titleData: ['Hot Water Pressure Washer'],
          //     pathData: ['11051607', '11051613'],
          //   },
          //   {},
          // ];
          break
        }
        case 3: {
          breadcrumbsData.push(
            {
              // Category Overview
              titleData: [categoryPath[0].name],
              pathData: [categoryPath[0].id],
            },
            {
              // Product Overview
              titleData: [categoryPath[1].name],
              pathData: [categoryPath[0].id, categoryPath[1].id],
            },
            {
              // Product Overview with class selected
              titleData: [categoryPath[2].name],
              pathData: [categoryPath[0].id, categoryPath[1].id, categoryPath[2].id],
            },
          )
          // Example result:
          // breadcrumbsData = [
          //   {
          //     titleData: ['Pressure Washers'],
          //     pathData: ['11051607'],
          //   },
          //   {
          //     titleData: ['Hot Water Pressure Washer'],
          //     pathData: ['11051607', '11051613'],
          //   },
          //   {
          //     titleData: ['Super class'],
          //     pathData: ['11051607', '11051613', '11051616'],
          //   },
          // ];
          break
        }
        default: {
          break
        }
      }
    }
    breadcrumbsData.push({
      titleData: [materialName],
      pathData: productTypeLinkPrefix === '/accessories' || productTypeLinkPrefix === '/detergents'
        ? [productTypeLinkPrefix.replace('/', ''), materialNumber]
        : [materialNumber],
    })
    setBreadcrumbsData(breadcrumbsData)
  }, [
    setIsTitleTranslated,
    setTitle,
    productTypeLinkPrefix,
    t,
    location.pathname,
    materialNumber,
    productType,
    categoryPath,
    setBreadcrumbsData,
    materialName,
  ])

  const renderProductNameArea = () => {
    return (
      <div className="product-details__title full-width-page-component__wrapper">
        <div className="product-details__title__container">
          <div className="product-details__title__item--left">
            <h2>
              {isLoadingProductDetail
                ? <Skeleton height={30} width={120} />
                : categoryPath?.[0]?.name || categoryName}
            </h2>
            <h1>{isLoadingProductDetail ? <Skeleton height={40} width={170} /> : materialName}</h1>
            <p className="product-details__title__item-number">
              {isLoadingProductDetail ? <Skeleton height={20} width={180} /> : (
                <span>
                  {t('productDetails.itemNo')}: {materialNumber}
                </span>
              )}
            </p>
          </div>
          <div className="product-details__title__item--right">{renderActionButtons()}</div>
        </div>
        {!isIpadLandscape && <br />}
        <ProductComparisonModal
          setShowModal={setShowProductComparisonModal}
          showModal={showProductComparisonModal}
          productsData={{
            materialNumber,
            materialName,
            categoryName,
            price: netPrice || listPrice,
            currencyUnit,
            image: getMainProductImage(productImages),
            displayListPrice: displayListPrice && hasViewListPricePermission,
          }}
        />
      </div>
    )
  }

  if (actionStatus === ACTION_STATUS.FAILED) {
    return <NotFound />
  }

  return (
    <div className="product-details__page__wrapper">
      {/* Product Name Area */}
      {isIpadLandscape && renderProductNameArea()}

      <div className="product-details__top__wrapper">
        {/* Product Carousel Area */}
        {isLoadingProductDetail
          ? (
            <div className="product-details__carousel product-details__carousel--loading">
              <Skeleton height={397} />
            </div>
          )
          : <ProductCarousel dataSrc={productImages} />}

        {/* Price Box Area */}
        <div className="price-box__container">
          <PriceBox
            currencyUnit={currencyUnit || undefined}
            deliveryDates={deliveryDates}
            hasMinQuantity={hasMinQuantity}
            isAvailable={isAvailable || true}
            isBProgramProduct={isBProgramProduct}
            isDangerous={isDangerous}
            isLoading={isLoadingPriceBox || isLoadingProductDetail}
            listingPrice={listPrice}
            materialName={materialName}
            materialNumber={materialNumber}
            price={netPrice}
            requiresInstruction={requiresInstruction}
            shouldDisplayListPrice={displayListPrice && hasViewListPricePermission}
          />
        </div>
      </div>

      {/* Product Name Area */}
      {!isIpadLandscape && renderProductNameArea()}

      {/* Product Detail Area */}
      <ProductDetail isLoading={isLoadingProductDetail} materialNumber={materialNumber} />
    </div>
  )
}

export default ProductDetailPage
